import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IpfsService } from '../../ipfs.service';
import { PolicyDetailsDialogComponent } from '../policy-details-dialog/policy-details-dialog.component';

export interface Asset {
  id: string;
  name: string;
  desc: string;
  balance: string;
}

interface Condition {
  subject: 'Sender' | 'Receiver';
  name: string;
  predicate: string;
  value: string | number | string[];
  issuers?: string[];
}

interface ConditionGroup {
  description?: string;
  composition: 'ALL' | 'ANY';
  conditions: Condition[];
}

interface PolicyJson {
  name?: string;
  description?: string;
  trustedIssuers?: string[];
  permanent?: boolean;
  nextConditionHash?: string;
  groupComposition: 'ALL' | 'ANY';
  groups: ConditionGroup[];
}

@Component({
  selector: 'ssi-asset-card',
  templateUrl: './asset-card.component.html',
  styleUrls: ['./asset-card.component.scss'],
})
export class AssetCardComponent implements OnInit {
  @Input() asset!: Asset;
  @Output() transfer = new EventEmitter<string>();

  isZeroToken: boolean = false;
  policySummary: string = '';
  hasChainedConditions: boolean = false;
  isPermanentPolicy: boolean = false;
  policyName: string = '';

  // Store full policy for details dialog
  private policy: PolicyJson | null = null;

  private readonly ZERO_TOKEN_ID = '0x0000000000000000000000000000000000000000000000000000000000000000';

  constructor(
    private ipfsService: IpfsService,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    this.isZeroToken = this.asset.id === this.ZERO_TOKEN_ID;

    if (!this.isZeroToken) {
      this.policySummary = 'Loading...';
      await this.loadPolicyDetails();
    }
  }

  async loadPolicyDetails(): Promise<void> {
    try {
      const policy = await this.ipfsService.getMeta(this.asset.id) as PolicyJson | null;

      if (!policy) {
        this.policySummary = 'Unable to load policy';
        return;
      }

      this.policy = policy;

      if (policy.name) {
        this.policyName = policy.name;
      }

      if (policy.permanent) {
        this.isPermanentPolicy = true;
      }

      if (policy.nextConditionHash) {
        this.hasChainedConditions = true;
      }

      this.policySummary = this.buildPolicySummary(policy);

    } catch (error) {
      console.error('Error loading policy details:', error);
      this.policySummary = 'Unable to load policy details';
    }
  }

  buildPolicySummary(policy: PolicyJson): string {
    if (policy.description) {
      return policy.description;
    }

    if (!policy.groups || policy.groups.length === 0) {
      return 'Policy-bound token';
    }

    let totalConditions = 0;
    policy.groups.forEach(g => {
      if (g.conditions) totalConditions += g.conditions.length;
    });

    return `${policy.groups.length} group(s), ${totalConditions} condition(s)`;
  }

  openPolicyDetails(event: Event): void {
    event.stopPropagation();

    this.dialog.open(PolicyDetailsDialogComponent, {
      data: {
        policy: this.policy,
        assetId: this.asset.id,
        policyName: this.policyName
      },
      width: '600px',
      maxHeight: '80vh'
    });
  }

  onTransfer(event: Event): void {
    event.stopPropagation();
    this.transfer.emit(this.asset.id);
  }
}
